import { Link, createInertiaApp, router } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createPinia } from 'pinia';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import VueDiff from 'vue-diff';
import VueTippy from 'vue-tippy';
import type { PluginOptions, TYPE } from 'vue-toastification';
import Toast, { POSITION, useToast } from 'vue-toastification';
import { ZiggyVue } from 'ziggy-js';

import type { ToastData } from '@/types/generated';

import Btn from './Components/Btn.vue';
import Icon from './Components/Icon.vue';
import AppLayout from './Layouts/App.vue';
import { installBrowserAuditListeners } from './lib/browserAudit';
import { hasPermission, hasRole } from './lib/usePermissions';
import { translate } from './util';

import '../css/app.css';
import 'vue-diff/dist/index.css';
import 'vue-toastification/dist/index.css';

void createInertiaApp({
    resolve: async (name) => {
        const page = await resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob<DefineComponent>('./Pages/**/*.vue'));
        page.default.layout = page.default.layout || AppLayout;
        return page;
    },

    setup({ el, App, props, plugin: InertiaPlugin }) {
        const pinia = createPinia();
        const app = createApp({ render: () => h(App, props) })
            .use(InertiaPlugin)
            .use(pinia)
            .use(ZiggyVue, Ziggy)
            .use(VueQueryPlugin)
            .use(VueTippy, {
                defaultProps: { theme: 'light-border' },
            })
            .use(VueDiff)
            .use(Toast, {
                icon: false,
                position: POSITION.BOTTOM_RIGHT,
            } satisfies PluginOptions)
            .mixin({ methods: { $can: hasPermission, $is: hasRole, __: translate } })
            .component('InertiaLink', Link)
            .component('Btn', Btn)
            .component('Icon', Icon);

        if (import.meta.env.PROD) {
            Sentry.init({
                app,
                dsn: 'https://6793c6f1110946cbba8efbcb746baf6b@o126420.ingest.sentry.io/6479108',
            });
        }

        installBrowserAuditListeners();

        app.mount(el);
    },
});

function isToastData(value: unknown): value is ToastData {
    return typeof value === 'object' && value !== null && 'message' in value && 'type' in value;
}

function isToastType(value: unknown): value is TYPE {
    return typeof value === 'string' && ['success', 'error', 'warning', 'info', 'default'].includes(value);
}

router.on('success', ({ detail: { page } }) => {
    const toast = useToast();
    const message: unknown = page.props.message;
    if (isToastData(message) && message.message) {
        toast(message.message, isToastType(message.type) ? { type: message.type } : {});
    }
});

if (import.meta.hot) {
    // Prevent full reload, see https://github.com/vitejs/vite/issues/5763#issuecomment-1974235806
    import.meta.hot.on('vite:beforeFullReload', (payload) => {
        payload.path = '(WORKAROUND).html';
    });
}
